import { useState } from "react";
import { AsistenteImportacion } from "../../components/importacion/AsistenteImportacion";
import { Aviso } from "../../components/ui/primitivos";
import {
  type CampoImportacion,
  importarProductos,
  type SimulacionProductos,
} from "../../lib/consola-api";

/**
 * Importación de productos y precios — porteada de `ImportProductosPage`.
 *
 * El flujo es el de clientes y vive en `AsistenteImportacion`. Lo propio de acá
 * son **dos cosas**, y las dos existen porque una fila de esta planilla no crea
 * una entidad sino hasta cinco:
 *
 * 1. **La vigencia.** Los precios importados arrancan en `vigenteDesde`, y los
 *    que reemplazan se cierran el día anterior (ADR 0012). Es una decisión de
 *    la importación entera, no de cada fila, así que va como control aparte.
 * 2. **El resumen de lo que se va a CREAR.** La planilla puede dar de alta
 *    entradas de catálogo, y ahí un `"Mediano "` con espacio al final ensucia un
 *    catálogo curado sin que nadie se entere. Verlo antes de confirmar es la
 *    salvaguarda, y por eso la lista va completa y no truncada.
 */
export function ImportarProductos({
  campos,
  onCerrar,
  onImportado,
}: {
  campos: readonly CampoImportacion[];
  onCerrar: () => void;
  onImportado: () => Promise<void>;
}) {
  const [vigenteDesde, setVigenteDesde] = useState("");
  const [vigenteHasta, setVigenteHasta] = useState("");

  return (
    <AsistenteImportacion<SimulacionProductos>
      titulo="Importar productos y precios"
      sustantivo="precio"
      campos={campos}
      importar={(filas, simular) =>
        importarProductos(filas, simular, {
          ...(vigenteDesde ? { vigenteDesde } : {}),
          ...(vigenteHasta ? { vigenteHasta } : {}),
        })
      }
      extras={
        <div className="space-y-2 rounded-md border border-neutral-200 p-3">
          <p className="text-neutral-700 text-sm">Vigencia de los precios importados</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-neutral-600 text-sm">
              <span className="block">Desde</span>
              <input
                type="date"
                value={vigenteDesde}
                onChange={(e) => setVigenteDesde(e.target.value)}
                className="mt-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-neutral-900 text-sm"
              />
            </label>
            <label className="text-neutral-600 text-sm">
              <span className="block">Hasta</span>
              <input
                type="date"
                value={vigenteHasta}
                onChange={(e) => setVigenteHasta(e.target.value)}
                className="mt-1 rounded-md border border-neutral-300 bg-white px-2 py-1 text-neutral-900 text-sm"
              />
            </label>
            <p className="text-neutral-500 text-xs">
              Vacío: desde hoy, sin fecha de fin. Los precios que estos reemplazan se cierran el día
              anterior.
            </p>
          </div>
        </div>
      }
      resumen={(simulacion) => <ResumenImportacion simulacion={simulacion} />}
      onCerrar={onCerrar}
      onImportado={onImportado}
    />
  );
}

function ResumenImportacion({ simulacion }: { simulacion: SimulacionProductos }) {
  const aCrear = simulacion.aCrear;
  if (!aCrear) return null;

  const nuevoEnCatalogo = aCrear.tamanosDeCatalogo.length > 0 || aCrear.basesDeCatalogo.length > 0;

  return (
    <div className="space-y-2">
      <div className="rounded-md border border-neutral-200 p-3 text-sm">
        <p className="text-neutral-700">
          Los precios rigen desde <strong>{simulacion.vigenteDesde}</strong>
          {simulacion.vigenteHasta ? ` hasta ${simulacion.vigenteHasta}` : " sin fecha de fin"}. Lo
          que reemplacen se cierra el <strong>{simulacion.fechaCierre}</strong>.
        </p>
        <ul className="mt-2 space-y-0.5 text-neutral-600">
          <li>{aCrear.precios} precio(s) a crear</li>
          {aCrear.productos.length > 0 ? (
            <li>
              Productos nuevos: <strong>{aCrear.productos.join(", ")}</strong>
            </li>
          ) : null}
        </ul>
      </div>

      {/* Un catálogo nuevo es lo que hay que mirar dos veces: se revisa a mano
          y nadie lo borra después. */}
      {nuevoEnCatalogo ? (
        <Aviso>
          Esta planilla agrega entradas al catálogo. Revisá que no sean un error de tipeo:
          {aCrear.tamanosDeCatalogo.length > 0
            ? ` tamaños — ${aCrear.tamanosDeCatalogo.join(", ")}.`
            : ""}
          {aCrear.basesDeCatalogo.length > 0
            ? ` bases — ${aCrear.basesDeCatalogo.join(", ")}.`
            : ""}
        </Aviso>
      ) : null}
    </div>
  );
}
